import React from 'react';
import { format } from 'date-fns';

import {
  clearVersionValues,
  copyVersionValues,
  currentVersion,
  moveProblem,
  moveStart,
  openYear,
  ProductVersion,
  readVersions,
  removeProblem,
  removeVersion,
  setName,
  splitAt,
  writeVersions,
} from './productVersions';

/** Local, not UTC: a UTC day key leaves a Brussels morning on yesterday's version until 02:00. */
export const todayKey = (): string => format(new Date(), 'yyyy-MM-dd');

export interface ProductVersions {
  /** Oldest first. */
  versions: ProductVersion[];
  /** The one on screen: what the Team and rates cards read and write. Null while the chain is empty. */
  version: ProductVersion | null;
  select: (number: string) => void;
  /** Splits the running version from this date. False when the date was refused. */
  create: (startDate: string, name: string) => boolean;
  /** Moves a version's start, and renames it. False when the date was refused. */
  update: (number: string, startDate: string, name: string) => boolean;
  remove: (number: string) => boolean;
  /** Bumped whenever a version is created, edited or deleted, so the cards re-read the store. */
  revision: number;
}

/** A year with no version is given one on the first read, so last year's rates never price this one. */
export const useProductVersions = (productId: string, reread?: string): ProductVersions => {
  const today = todayKey();
  const [revision, setRevision] = React.useState(0);
  const [chosen, setChosen] = React.useState<string | null>(null);

  // `reread` is another instance's writes: this one's `revision` cannot know a second hook saved.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const versions = React.useMemo(() => readVersions(productId), [productId, revision, reread]);

  React.useEffect(() => {
    const opened = openYear(readVersions(productId), today);
    if (!opened.created) return;
    // Into every year opened, or a year skipped over reads the unversioned fallback, not what it inherited.
    if (opened.from)
      opened.opened.forEach(version => copyVersionValues(productId, opened.from!.number, version.number));
    writeVersions(productId, opened.versions);
    setRevision(current => current + 1);
  }, [productId, today]);

  // A product switch is a different set of versions, so the choice made on the last one is dropped.
  React.useEffect(() => {
    setChosen(null);
  }, [productId]);

  const version = versions.find(each => each.number === chosen) ?? currentVersion(versions, today);

  const saved = (next: ProductVersion[], effects: () => void): void => {
    effects();
    writeVersions(productId, next);
    setRevision(current => current + 1);
  };

  const create = (startDate: string, name: string): boolean => {
    const split = splitAt(versions, startDate, name);
    if (!split.created) return false;
    const from = versions[versions.length - 1].number;
    const to = split.created.number;
    saved(split.versions, () => copyVersionValues(productId, from, to));
    // The new version is the one being filled in, so the tab lands on it.
    setChosen(to);
    return true;
  };

  const update = (number: string, startDate: string, name: string): boolean => {
    const target = versions.find(each => each.number === number);
    if (!target) return false;
    const moving = startDate !== target.startDate;
    if (moving && moveProblem(versions, number, startDate)) return false;
    const next = setName(moving ? moveStart(versions, number, startDate) : versions, number, name);
    saved(next, () => undefined);
    return true;
  };

  const remove = (number: string): boolean => {
    if (removeProblem(versions, number)) return false;
    saved(removeVersion(versions, number), () => undefined);
    clearVersionValues(productId, number);
    if (chosen === number) setChosen(null);
    return true;
  };

  return { versions, version, select: setChosen, create, update, remove, revision };
};
